import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Download,
  Code,
  Globe,
  AlertTriangle,
} from "lucide-react";
import Layout from "../../components/layout";
import { apiService } from "../../services";
import { BookCover } from "../../components/common";

const siteMetadata = {
  title: "Designing Multi-Agent Systems",
  description:
    "Access your book downloads, manage your account, and explore exclusive content for Designing Multi-Agent Systems",
  author: "Victor Dibia",
  image: "/images/og-service.png",
  keywords: "multi-agent, customer platform, dashboard, payments",
  twitterUsername: "@designingwithml",
};

// Reusable Components
const PaymentConfirmation: React.FC<{
  sessionStatus: any;
}> = ({ sessionStatus }) => {
  if (!sessionStatus) return null;

  return (
    <>
      {sessionStatus.payment_status === "paid" &&
        sessionStatus.customer_email && (
          <p className="text-lg text-secondary mb-4">
            Confirmation sent to{" "}
            <span className="font-semibold text-accent">
              {sessionStatus.customer_email}
            </span>
          </p>
        )}

      {sessionStatus.amount_total > 0 && (
        <div className="text-sm text-secondary mb-6">
          Payment of ${(sessionStatus.amount_total / 100).toFixed(2)} confirmed
        </div>
      )}
    </>
  );
};

const WhatsNextSteps: React.FC<{
  className?: string;
}> = ({ className = "" }) => {
  const navigate = useNavigate();

  const steps = [
    {
      number: 1,
      title: "Check your email",
      description:
        "We've sent you a welcome email with your secure login link.",
      fullDescription:
        "Check your inbox (and spam folder) for login instructions.",
    },
    {
      number: 2,
      title: "Access your dashboard",
      description: "Click the login link to access your downloads.",
      fullDescription:
        "Click the login link in your email to access your customer dashboard where you can download your book and manage your account.",
    },
    {
      number: 3,
      title: "Start learning",
      description: "Download your book and begin your journey.",
      fullDescription:
        "Download your book in PDF or EPUB format, access the sample code repository, and begin your Multi-Agent Systems journey.",
    },
  ];

  return (
    <div
      className={`bg-primary rounded-lg border border-secondary p-6 lg:p-8 ${className}`}
    >
      <h2 className="text-2xl font-semibold text-primary mb-6">
        What happens next?
      </h2>
      <div className="space-y-6">
        {steps.map((step) => (
          <div key={step.number} className="flex items-start text-left">
            <div className="bg-secondary text-primary rounded-full w-8 h-8 flex items-center justify-center font-semibold mr-4 mt-1 flex-shrink-0">
              {step.number}
            </div>
            <div>
              <h3 className="font-semibold text-primary mb-2">{step.title}</h3>
              <p className="text-secondary">
                <span className="lg:hidden">{step.description}</span>
                <span className="hidden lg:inline">{step.fullDescription}</span>
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Support link for desktop */}
      <div className="hidden lg:block mt-8  text-secondary">
        <p>
          Didn't receive an email? Check your spam folder or
          <a
            href="https://github.com/victordibia/designing-multiagent-systems/issues"
            target="_blank"
            rel="noreferrer"
            className="text-accent hover:underline ml-1 inline"
          >
            reach out on GitHub
          </a>
          .
        </p>
      </div>
    </div>
  );
};

const DashboardButton: React.FC<{
  sessionStatus: any;
  onClick: () => void;
  className?: string;
}> = ({ sessionStatus, onClick, className = "" }) => {
  const hasImmediateAccess = sessionStatus?.immediate_access;

  return (
    <button
      onClick={onClick}
      className={`bg-accent text-white text-invert py-2 px-6 rounded-md  hover:brightness-110 transition-all ${className}`}
    >
      {hasImmediateAccess ? "Access Your Dashboard Now" : "Go to Dashboard"}
    </button>
  );
};

type DownloadItem = {
  id: string;
  title: string;
  type: string;
  url: string;
  language?: string;
  isTranslation?: boolean;
};

const LANGUAGE_NAMES: Record<string, { name: string; native: string }> = {
  en: { name: "English", native: "English" },
  fr: { name: "French", native: "Francais" },
  de: { name: "German", native: "Deutsch" },
  es: { name: "Spanish", native: "Espanol" },
  ja: { name: "Japanese", native: "日本語" },
  zh: { name: "Chinese", native: "中文" },
};

const DownloadCard: React.FC<{
  download: DownloadItem;
  translations?: DownloadItem[];
}> = ({ download, translations }) => {
  const [selectedLang, setSelectedLang] = useState<string>("");

  const selectedTranslation = translations?.find(
    (t) => t.language === selectedLang
  );

  const formatBadge = download.type === "bookpdf" ? "PDF" : download.type === "bookepub" ? "EPUB" : null;
  const badgeColor = download.type === "bookpdf" ? "bg-red-600" : "bg-blue-600";

  return (
    <div className="border border-tertiary rounded-lg p-3 sm:p-4 bg-secondary/20">
      <div className="flex items-start space-x-3 sm:space-x-4">
        {/* Book cover thumbnail */}
        {(download.type === "bookpdf" || download.type === "bookepub") ? (
          <div className="flex-shrink-0 w-12 h-16 sm:w-16 sm:h-20 rounded overflow-hidden relative">
            <img
              src="/images/bookcover.webp"
              alt="Book Cover"
              className="w-full h-full object-cover"
            />
            {formatBadge && (
              <div className={`absolute bottom-0 right-0 ${badgeColor} text-white text-xs px-1 py-0.5 rounded-tl-md font-medium`}>
                {formatBadge}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-shrink-0 w-12 h-16 sm:w-16 sm:h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center text-white">
            <Code className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-base sm:text-lg font-semibold text-primary mb-1 leading-tight">
            {download.title}
          </h4>

          {/* Download button */}
          <a
            href={download.url}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 w-full bg-accent text-white px-4 py-2.5 rounded-lg hover:bg-accent/90 transition-colors font-medium flex items-center justify-center gap-2 text-sm"
          >
            <Download className="w-4 h-4" />
            Download
          </a>

          {/* Translations section */}
          {translations && translations.length > 0 && (
            <div className="mt-3 pt-3 border-t border-tertiary/50">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-secondary" />
                <span className="text-xs font-medium text-secondary">
                  Also available in:
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={selectedLang}
                  onChange={(e) => setSelectedLang(e.target.value)}
                  className="flex-1 min-w-0 text-sm border border-tertiary rounded-md px-2 py-1.5 bg-primary text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                >
                  <option value="">Select language...</option>
                  {translations.map((t) => (
                    <option key={t.id} value={t.language}>
                      {LANGUAGE_NAMES[t.language || ""]?.native || t.language}{" "}
                      ({LANGUAGE_NAMES[t.language || ""]?.name || t.language})
                    </option>
                  ))}
                </select>

                {selectedTranslation && (
                  <a
                    href={selectedTranslation.url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 bg-secondary/50 text-primary rounded-md hover:bg-secondary transition-colors text-sm font-medium flex items-center gap-1"
                  >
                    <Download className="w-3 h-3" />
                    Download
                  </a>
                )}
              </div>

              <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-600">
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>Auto-translated. May contain errors.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DownloadButtons: React.FC<{
  downloadUrls: DownloadItem[];
  className?: string;
}> = ({ downloadUrls, className = "" }) => {
  if (!downloadUrls || downloadUrls.length === 0) return null;

  // Group: English/primary downloads + translations by type
  const { primary, translationsByType } = useMemo(() => {
    const primary = downloadUrls.filter(
      (d) => !d.isTranslation && (!d.language || d.language === "en")
    );
    const translations = downloadUrls.filter(
      (d) => d.isTranslation || (d.language && d.language !== "en")
    );

    const translationsByType: Record<string, DownloadItem[]> = {};
    for (const t of translations) {
      if (!translationsByType[t.type]) {
        translationsByType[t.type] = [];
      }
      translationsByType[t.type].push(t);
    }

    return { primary, translationsByType };
  }, [downloadUrls]);

  return (
    <div className={className}>
      <h3 className="font-semibold text-primary mb-3">
        Your Downloads Are Ready!
      </h3>
      <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
        {primary.map((download) => (
          <DownloadCard
            key={download.id}
            download={download}
            translations={translationsByType[download.type]}
          />
        ))}
      </div>
    </div>
  );
};

const ImmediateAccessSection: React.FC<{
  sessionStatus: any;
  className?: string;
  variant?: "mobile" | "desktop";
}> = ({ sessionStatus, className = "", variant = "desktop" }) => {
  if (!sessionStatus?.immediate_access) return null;

  const isMobile = variant === "mobile";

  return (
    <div className={className}>
      {sessionStatus.immediate_access.expired ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 lg:p-6">
          <h3 className="font-semibold text-yellow-800 mb-2 lg:mb-3">
            <>
              <Download className="inline-block mr-2" /> Download instructions
              sent to your email!
            </>
          </h3>
          <p className="text-yellow-700 text-sm lg:text-base">
            Your immediate download window has expired, but don't worry - we've
            sent login instructions to your email
            {isMobile ? "." : " where you can access your downloads anytime."}
          </p>
        </div>
      ) : sessionStatus.immediate_access.download_urls ? (
        <DownloadButtons
          downloadUrls={sessionStatus.immediate_access.download_urls}
        />
      ) : null}
    </div>
  );
};

const LifetimeAccessBadge: React.FC = () => (
  <div className="bg-secondary border border-tertiary rounded-lg p-6">
    <h3 className="font-semibold text-primary mb-2">
      Lifetime Access Included
    </h3>
    <ul className="text-secondary text-sm space-y-2">
      <li>• PDF and EPUB formats</li>
      <li>• Sample code repository</li>
      <li>• All future updates</li>
    </ul>
  </div>
);

// Success page after purchase
const SuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionStatus, setSessionStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkSessionStatus = async () => {
      try {
        // Get session_id from URL parameters
        const sessionId = searchParams.get("session_id");

        if (!sessionId) {
          // No session ID, redirect to home or show generic success
          setLoading(false);
          return;
        }

        // Fetch session status with immediate access request
        const sessionData = await apiService.getCheckoutSessionStatus(
          sessionId,
          true // Request immediate access
        );

        setSessionStatus(sessionData);
        setLoading(false);

        // If session is not complete, handle accordingly
        if (sessionData.status === "open") {
          navigate("/pricing");
          return;
        }

        // No auto-login needed - user can access downloads directly or use email login
      } catch (err) {
        setError("Failed to verify payment status");
        setLoading(false);
      }
    };

    checkSessionStatus();
  }, []);

  if (loading) {
    return (
      <Layout meta={siteMetadata} title="Verifying Payment..." link="/success">
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold mb-4">
              Verifying your payment...
            </h1>
            <p className="text-secondary">
              Please wait while we confirm your purchase.
            </p>
          </div>
        </main>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout
        meta={siteMetadata}
        title="Payment Verification Error"
        link="/success"
      >
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-6xl mb-6">⚠️</div>
            <h1 className="text-4xl font-bold mb-4 text-red-600">
              Payment Verification Error
            </h1>
            <p className="text-xl text-secondary mb-8">{error}</p>
            <p className="text-secondary mb-8">
              If you believe this is an error, please report it on the GitHub
              repository with your order details.
            </p>
            <a
              href="https://github.com/victordibia/designing-multiagent-systems/issues"
              target="_blank"
              rel="noreferrer"
              className="bg-accent text-white text-invert py-2 px-6 rounded-md font-semibold hover:brightness-110 transition-all mr-4"
            >
              Report on GitHub
            </a>
            <button
              onClick={() => navigate("/pricing")}
              className="bg-secondary text-primary py-2 px-6 rounded-md font-semibold hover:brightness-110 border border-tertiary transition-all"
            >
              Try Again
            </button>
          </div>
        </main>
      </Layout>
    );
  }
  return (
    <Layout meta={siteMetadata} title="Purchase Successful!" link="/success">
      <main className="container mx-auto px-4 py-12">
        {/* Mobile Layout */}
        <div className="lg:hidden">
          <div className="text-center mb-8">
            <div className="text-6xl mb-6">🎉</div>
            <h1 className="text-4xl font-bold mb-4 text-primary">
              Purchase Successful!
            </h1>
            <p className="text-xl text-secondary mb-6">
              Welcome to your Multi-Agent Systems journey!
            </p>

            <PaymentConfirmation sessionStatus={sessionStatus} />

            {/* Download Buttons or Expired Message - Mobile */}
            <ImmediateAccessSection
              sessionStatus={sessionStatus}
              variant="mobile"
              className="mb-6"
            />

            <div className="flex justify-center mb-6">
              <BookCover size="mobile" />
            </div>
          </div>

          {/* Mobile What's Next */}
          <WhatsNextSteps className="mb-8" />

          {/* Mobile CTA */}
          <div className="text-center mb-8">
            <DashboardButton
              sessionStatus={sessionStatus}
              onClick={() => navigate("/dashboard")}
              className="w-full mb-4"
            />
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:block">
          <div className="flex gap-8 items-start">
            {/* Left Side - What's Next */}
            <div className="flex-1">
              <div className="mb-8">
                <div className="text-6xl mb-6">🎉</div>
                <h1 className="text-5xl font-bold mb-3 text-primary">
                  Purchase Successful!
                </h1>
                <p className="text-xl text-secondary mb-4">
                  Welcome to your Multi-Agent Systems journey!
                </p>

                <PaymentConfirmation sessionStatus={sessionStatus} />
              </div>

              {/* Download Buttons or Expired Message - Desktop */}
              <ImmediateAccessSection
                sessionStatus={sessionStatus}
                variant="desktop"
                className="mb-8"
              />

              {/* What's Next */}
              <WhatsNextSteps className="mb-8" />

              <div className="mt-8 text-sm text-secondary">
                <p>
                  Didn't receive an email? Check your spam folder or
                  <a
                    href="https://github.com/victordibia/designing-multiagent-systems/issues"
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent hover:underline ml-1 inline"
                  >
                    reach out on GitHub
                  </a>
                  .
                </p>
              </div>
            </div>

            {/* Right Side - Book Cover & Lifetime Access */}
            <div className="w-80 flex-shrink-0">
              <div className="  top-8">
                <div className="text-center mb-6">
                  <div className="flex justify-center mb-4">
                    <BookCover size="desktop" width="w-80" />
                  </div>

                  {/* CTA Button under book cover */}
                  <div className="mb-6">
                    <DashboardButton
                      sessionStatus={sessionStatus}
                      onClick={() => navigate("/dashboard")}
                      className="w-full text-lg"
                    />
                  </div>

                  <div>
                    <p className="text-secondary text-sm mb-4">
                      You now have lifetime access to all future updates of this
                      book. Download the latest version anytime from your
                      dashboard.
                    </p>
                  </div>
                </div>

                <div className="bg-secondary border border-tertiary rounded-lg p-6">
                  <h3 className="font-semibold text-primary mb-2">
                    Lifetime Access Included
                  </h3>
                  <ul className="text-secondary text-sm space-y-2">
                    <li>• PDF and EPUB formats</li>
                    <li>• Sample code repository</li>
                    <li>• All future updates</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default SuccessPage;
